/**
 * Tabla de listado de cuentas para el BO.
 *
 * @taskId T473, T464
 * @see functional/Specs/specs_cycle_4.md HU-27-FE, HU-15-FE
 */

import { useTranslation } from 'react-i18next';
import { Eye, History, Pencil, XCircle } from 'lucide-react';
import {
  Badge,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@flexibilitypay/ui';

import type { AccountStatus, AccountSummary } from '../types/account.types';

export interface AccountListTableProps {
  accounts: AccountSummary[];
  isLoading: boolean;
  onViewDetail: (account: AccountSummary) => void;
  onViewHistory: (account: AccountSummary) => void;
  onEditStatus: (account: AccountSummary) => void;
  canEditStatus: boolean;
  /** HU-15-FE: Handler para cierre involuntario (solo PSP_ADMIN) */
  onCloseAccount?: (account: AccountSummary) => void;
  /** HU-15-FE: Si el usuario puede cerrar cuentas */
  canCloseAccount?: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const STATUS_BADGE_VARIANTS: Record<AccountStatus, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  ACTIVE: 'default',
  SUSPENDED: 'secondary',
  BLOCKED: 'destructive',
  DEACTIVATED: 'outline',
  CLOSED: 'outline',
};

const STATUS_LABELS: Record<AccountStatus, string> = {
  ACTIVE: 'Activa',
  SUSPENDED: 'Suspendida',
  BLOCKED: 'Bloqueada',
  DEACTIVATED: 'Desactivada',
  CLOSED: 'Cerrada',
};

function formatCVU(cvu: string): string {
  // Formato: XXXX XXXX XXXX XXXX XXXX XX
  return cvu.replace(/(\d{4})(?=\d)/g, '$1 ').trim();
}

function formatMoney(amount: string): string {
  const num = parseFloat(amount);
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    minimumFractionDigits: 2,
  }).format(num);
}

export function AccountListTable({
  accounts,
  isLoading,
  onViewDetail,
  onViewHistory,
  onEditStatus,
  canEditStatus,
  onCloseAccount,
  canCloseAccount = false,
  currentPage,
  totalPages,
  onPageChange,
}: AccountListTableProps) {
  const { t } = useTranslation(['accounts', 'common']);

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('accounts:list.columns.cvu', 'CVU')}</TableHead>
              <TableHead>{t('accounts:list.columns.merchant', 'Merchant')}</TableHead>
              <TableHead>{t('accounts:list.columns.tipo', 'Tipo')}</TableHead>
              <TableHead>{t('accounts:list.columns.moneda', 'Moneda')}</TableHead>
              <TableHead className="text-right">{t('accounts:list.columns.saldo', 'Saldo')}</TableHead>
              <TableHead>{t('accounts:list.columns.estado', 'Estado')}</TableHead>
              <TableHead className="text-right">{t('accounts:list.columns.acciones', 'Acciones')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell><Skeleton className="h-4 w-48" /></TableCell>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                <TableCell><Skeleton className="h-8 w-24" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  // Estado vacío
  if (accounts.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center">
        <p className="text-muted-foreground">
          {t('accounts:list.empty', 'No se encontraron cuentas con los filtros aplicados.')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('accounts:list.columns.cvu', 'CVU')}</TableHead>
              <TableHead>{t('accounts:list.columns.merchant', 'Merchant')}</TableHead>
              <TableHead>{t('accounts:list.columns.tipo', 'Tipo')}</TableHead>
              <TableHead>{t('accounts:list.columns.moneda', 'Moneda')}</TableHead>
              <TableHead className="text-right">{t('accounts:list.columns.saldo', 'Saldo')}</TableHead>
              <TableHead>{t('accounts:list.columns.estado', 'Estado')}</TableHead>
              <TableHead className="text-right">{t('accounts:list.columns.acciones', 'Acciones')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map((account) => (
              <TableRow
                key={account.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => onViewDetail(account)}
              >
                <TableCell className="font-mono text-xs">
                  <Tooltip>
                    <TooltipTrigger>{formatCVU(account.cvu)}</TooltipTrigger>
                    <TooltipContent>
                      <p>Alias: {account.alias}</p>
                    </TooltipContent>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium">
                      {account.merchantRazonSocial.substring(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium">{account.merchantRazonSocial}</p>
                      <p className="text-xs text-muted-foreground">ID: {account.merchantId}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{account.tipo}</TableCell>
                <TableCell className="text-muted-foreground">{account.moneda}</TableCell>
                <TableCell className="text-right font-mono">
                  {formatMoney(account.saldo)}
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_BADGE_VARIANTS[account.estado]}>
                    {STATUS_LABELS[account.estado]}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewDetail(account);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Ver detalle</TooltipContent>
                    </Tooltip>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewHistory(account);
                          }}
                        >
                          <History className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Ver historial</TooltipContent>
                    </Tooltip>

                    {canEditStatus && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEditStatus(account);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Editar estado</TooltipContent>
                      </Tooltip>
                    )}

                    {/* HU-15-FE: Botón de cierre involuntario — solo PSP_ADMIN y cuentas ACTIVE */}
                    {canCloseAccount && onCloseAccount && account.estado === 'ACTIVE' && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            onClick={(e) => {
                              e.stopPropagation();
                              onCloseAccount(account);
                            }}
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Cierre involuntario</TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
          >
            {t('common:pagination.previous', 'Anterior')}
          </Button>
          <span className="text-sm text-muted-foreground">
            {t('common:pagination.pageOf', 'Página {{current}} de {{total}}', {
              current: currentPage + 1,
              total: totalPages,
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
          >
            {t('common:pagination.next', 'Siguiente')}
          </Button>
        </div>
      )}
    </div>
  );
}
